export const DEFAULT_PRICING_SETTINGS = {
    exchangeRateEur: 5.35,
    exchangeRatePln: 1.3,
    exchangeRateUsd: 4.8,
    vatRate: 1.21,
};

/** Excel column AJ: preț listă EUR fără TVA = AF / 5 (fixed divisor in legacy sheet). */
export const LIST_PRICE_EURO_DIVISOR = 5;

/**
 * @param {Record<string, unknown>|null|undefined} rawSettings
 * @returns {typeof DEFAULT_PRICING_SETTINGS}
 */
export function mergeListPricingSettings(rawSettings) {
    const settings = rawSettings || {};
    return {
        exchangeRateEur: settings.exchangeRateEur ?? DEFAULT_PRICING_SETTINGS.exchangeRateEur,
        exchangeRatePln: settings.exchangeRatePln
            ?? settings.exchangeRateRon
            ?? DEFAULT_PRICING_SETTINGS.exchangeRatePln,
        exchangeRateUsd: settings.exchangeRateUsd ?? DEFAULT_PRICING_SETTINGS.exchangeRateUsd,
        vatRate: settings.vatRate ?? DEFAULT_PRICING_SETTINGS.vatRate,
    };
}

/**
 * Baseline FX rates for products on the list (updated only when lista în lucru
 * is applied). Cancel-FX reverts the header to these rates.
 * @param {Record<string, unknown>|null|undefined} rawSettings
 * @returns {{ exchangeRateEur: number, exchangeRatePln: number, exchangeRateUsd: number }}
 */
export function resolveAppliedListExchangeRates(rawSettings) {
    const settings = rawSettings || {};
    const applied = settings.appliedExchangeRates;
    if (applied && typeof applied === 'object') {
        const eur = Number(applied.exchangeRateEur);
        const pln = Number(applied.exchangeRatePln ?? applied.exchangeRateRon);
        const usd = Number(applied.exchangeRateUsd);
        return {
            exchangeRateEur: Number.isFinite(eur) && eur > 0
                ? eur
                : DEFAULT_PRICING_SETTINGS.exchangeRateEur,
            exchangeRatePln: Number.isFinite(pln) && pln > 0
                ? pln
                : DEFAULT_PRICING_SETTINGS.exchangeRatePln,
            exchangeRateUsd: Number.isFinite(usd) && usd > 0
                ? usd
                : DEFAULT_PRICING_SETTINGS.exchangeRateUsd,
        };
    }
    const merged = mergeListPricingSettings(settings);
    return {
        exchangeRateEur: merged.exchangeRateEur,
        exchangeRatePln: merged.exchangeRatePln,
        exchangeRateUsd: merged.exchangeRateUsd,
    };
}

/**
 * @param {{ exchangeRateEur?: unknown, exchangeRatePln?: unknown, exchangeRateUsd?: unknown }} rates
 * @returns {{ exchangeRateEur: number, exchangeRatePln: number, exchangeRateUsd: number }}
 */
export function normalizeAppliedListExchangeRates(rates) {
    const eur = Number(rates?.exchangeRateEur);
    const pln = Number(rates?.exchangeRatePln);
    const usd = Number(rates?.exchangeRateUsd);
    return {
        exchangeRateEur: Number.isFinite(eur) && eur > 0
            ? eur
            : DEFAULT_PRICING_SETTINGS.exchangeRateEur,
        exchangeRatePln: Number.isFinite(pln) && pln > 0
            ? pln
            : DEFAULT_PRICING_SETTINGS.exchangeRatePln,
        exchangeRateUsd: Number.isFinite(usd) && usd > 0
            ? usd
            : DEFAULT_PRICING_SETTINGS.exchangeRateUsd,
    };
}

import {
    mapSystemToCategory,
    normalizeSystemValue,
    syncCategoriesFromSystem,
    syncCategoriesFromSystems,
    SYSTEM_DERIVED_CATEGORIES,
} from '@/data/productCategories';
import {
    cloneTransportCostBreakdown,
    createDefaultTransportCostBreakdown,
} from './transportCostBreakdown';
import {
    cloneStampDutyBreakdown,
    createDefaultStampDutyBreakdown,
    getStampDutyAmountRon,
} from './stampDutyBreakdown';

export { mapSystemToCategory, syncCategoriesFromSystem, syncCategoriesFromSystems, SYSTEM_DERIVED_CATEGORIES };

/**
 * @param {unknown} value
 * @returns {number}
 */
export function parseNumericInput(value) {
    if (value === null || value === undefined || value === '') {
        return 0;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }
    let normalized = String(value).trim().replace(/\s/g, '');
    const hasComma = normalized.includes(',');
    const hasDot = normalized.includes('.');

    // ro-RO format:
    // - decimal separator: ','
    // - thousands separator: '.'
    if (hasComma && hasDot) {
        // e.g. "1.234,56" -> "1234.56"
        normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else if (hasComma) {
        // e.g. "47,47" -> "47.47"
        normalized = normalized.replace(',', '.');
    } else if (hasDot) {
        // When there is no comma, the dot can mean either:
        // - thousands separators (e.g. "3.200" -> 3200)
        // - decimal separator for EN-style inputs (e.g. "12.5" -> 12.5)
        // If it matches the "groups of 3 digits" thousands pattern, strip dots.
        // Leading zero (e.g. "0.356") is always a decimal fraction, not ro-RO thousands.
        if (/^[+-]?\d{1,3}(\.\d{3})+$/.test(normalized)) {
            const integerPart = normalized.replace(/^[+-]/, '').split('.')[0];
            if (integerPart !== '0') {
                normalized = normalized.replace(/\./g, '');
            }
        }
        // Otherwise keep the dot so parseFloat can treat it as a decimal separator.
    }
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * @param {number} value
 * @param {number} [decimals]
 * @returns {number}
 */
export function roundMoney(value, decimals = 2) {
    if (!Number.isFinite(value)) {
        return 0;
    }
    const factor = 10 ** decimals;
    return Math.round((value + Number.EPSILON) * factor) / factor;
}

/**
 * @param {unknown} value
 * @param {number} [decimals]
 * @returns {string}
 */
export function formatDecimalValue(value, decimals = 2) {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    const numeric = typeof value === 'number' ? value : parseNumericInput(value);
    if (!Number.isFinite(numeric)) {
        return '';
    }
    return numeric.toLocaleString('ro-RO', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
}

/**
 * Formats numeric values for editable fields (comma decimal separator, ro-RO).
 * @param {unknown} value
 * @param {number} [decimals]
 * @returns {string}
 */
export function formatNumericInputValue(value, decimals = 2) {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    const numeric = typeof value === 'number' ? value : parseNumericInput(value);
    if (!Number.isFinite(numeric)) {
        return '';
    }
    return numeric.toLocaleString('ro-RO', {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
    });
}

/** Pricing form scalar fields displayed as comma-decimal numeric strings. */
export const PRICING_NUMERIC_FORM_FIELDS = {
    supplierListPriceOld: 4,
    supplierListPriceNew: 4,
    transportCostEstimated: 2,
    stampDutyAmount: 2,
    listPriceRonIncVatReference: 2,
    manualListPriceRonIncVatFinal: 2,
    overrideListPriceRonIncVatFinalValue: 2,
};

/**
 * @param {unknown} value
 * @param {number} [decimals]
 * @returns {string}
 */
export function formatPercentValue(value, decimals = 2) {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    const numeric = typeof value === 'number' ? value : parseNumericInput(value);
    if (!Number.isFinite(numeric)) {
        return '';
    }
    const percent = numeric * 100;
    return `${formatNumericInputValue(percent, decimals)}%`;
}

/**
 * @param {string} key
 * @param {unknown} value
 * @param {string} [format]
 * @returns {string}
 */
export function formatPricingResultValue(key, value, format = '') {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    // Unset list prices stay blank (do not show 0,00 when adaos/listă not filled yet).
    if (
        (key === 'listPriceRonExVat'
            || key === 'listPriceRonIncVatFinal'
            || key === 'listPriceEuroExVat')
        && !(Number(value) > 0)
    ) {
        return '';
    }

    if (format === 'percent1') {
        return formatPercentValue(value, 1);
    }
    if (format === 'percent2') {
        return formatPercentValue(value, 2);
    }
    if (format === 'decimal4') {
        return formatDecimalValue(value, 4);
    }
    if (format === 'money2') {
        return formatDecimalValue(value, 2);
    }

    if (key === 'priceChangeRatio' || key === 'computedPriceAdjustmentRatio' || key === 'suggestedPriceAdjustmentRatio') {
        return formatPercentValue(value, 2);
    }
    if (key === 'markupActual') {
        return formatPercentValue(value, 2);
    }
    if (key === 'netEntryPriceExVat' || key === 'appliedExchangeRate' || key === 'displayExchangeRate') {
        return formatDecimalValue(value, 4);
    }
    if (key === 'finalEntryPriceExVat') {
        return formatDecimalValue(value, 2);
    }

    return formatDecimalValue(value, 2);
}

/**
 * Form fields where users enter percentages (85) but calculations use ratios (0.85).
 */
export const PERCENTAGE_RATIO_FORM_FIELDS = [
    'discount1',
    'discount2',
    'cashDiscount',
    'marketingDiscount',
    'transportCostRate',
    'markupReference',
    'markupActual',
    'priceAdjustmentRatio',
];

export const SYSTEM_FORM_FIELDS = ['system1', 'system2', 'system3'];

export const DISCOUNT_FORM_FIELDS = ['discount1', 'discount2', 'cashDiscount'];

/** Tooltip labels for applied discount fields stored as ratios on pricing_detail. */
export const APPLIED_DISCOUNT_DISPLAY_FIELDS = [
    { key: 'discount1', label: 'Discount 1', hasNote: true },
    { key: 'discount2', label: 'Discount 2', hasNote: true },
    { key: 'cashDiscount', label: 'Discount cash', hasNote: true },
    { key: 'marketingDiscount', label: 'Discount marketing', hasNote: false },
];

/**
 * Collects non-zero discount lines from a stored furnizor pricing / pricing_detail.
 * Expects ratio values (0.1 = 10%), as persisted in pricing_detail.
 *
 * @param {Object|null|undefined} pricing
 * @returns {{ hasDiscounts: boolean, tooltip: string }}
 */
export function resolveAppliedDiscountsDisplay(pricing) {
    if (!pricing || typeof pricing !== 'object') {
        return { hasDiscounts: false, tooltip: '' };
    }

    const notes = parseStoredDiscountNotes(pricing.discountNotes);
    const lines = [];
    let noteIndex = 0;

    for (const field of APPLIED_DISCOUNT_DISPLAY_FIELDS) {
        const raw = pricing[field.key];
        if (raw === null || raw === undefined || raw === '') {
            continue;
        }
        const ratio = parseNumericInput(raw);
        if (!Number.isFinite(ratio) || ratio <= 0) {
            continue;
        }
        const percentText = formatPercentValue(ratio);
        if (!percentText) {
            continue;
        }
        let note = '';
        if (field.hasNote) {
            note = String(notes[noteIndex] ?? '').trim();
            noteIndex += 1;
        }
        lines.push(
            note
                ? `${field.label}: ${percentText} — ${note}`
                : `${field.label}: ${percentText}`,
        );
    }

    return {
        hasDiscounts: lines.length > 0,
        tooltip: lines.join('\n'),
    };
}

/**
 * Normalizes pricing-related form values to ro-RO comma decimal format.
 * @param {Record<string, unknown>} form
 */
export function normalizePricingFormNumericDisplay(form) {
    if (!form) {
        return;
    }

    Object.entries(PRICING_NUMERIC_FORM_FIELDS).forEach(([field, decimals]) => {
        const raw = form[field];
        if (raw === null || raw === undefined || String(raw).trim() === '') {
            return;
        }
        form[field] = formatNumericInputValue(raw, decimals);
    });

    PERCENTAGE_RATIO_FORM_FIELDS.forEach((field) => {
        if (field === 'priceAdjustmentRatio') {
            return;
        }
        const raw = form[field];
        if (raw === null || raw === undefined || String(raw).trim() === '') {
            return;
        }
        const percentDecimals = 2;
        form[field] = formatNumericInputValue(parseNumericInput(raw), percentDecimals);
    });

    if (Array.isArray(form.discounts)) {
        form.discounts = form.discounts.map((value) => {
            if (value === null || value === undefined || String(value).trim() === '') {
                return value ?? '';
            }
            return formatNumericInputValue(parseNumericInput(value), 2);
        });
    }

    if (Array.isArray(form.furnizorPricings)) {
        form.furnizorPricings.forEach((row) => {
            if (row && row !== form) {
                normalizePricingFormNumericDisplay(row);
            }
        });
    }
}

/**
 * @param {Record<string, unknown>} form
 * @returns {string[]}
 */
export function systemsArrayFromForm(form) {
    if (Array.isArray(form.systems) && form.systems.length) {
        return form.systems
            .map((value) => normalizeSystemValue(value) || String(value || '').trim())
            .filter(Boolean);
    }
    return SYSTEM_FORM_FIELDS
        .map((field) => normalizeSystemValue(form[field]) || String(form[field] || '').trim())
        .filter(Boolean);
}

/**
 * @param {Record<string, unknown>} form
 * @returns {string[]}
 */
export function discountsArrayFromForm(form) {
    if (Array.isArray(form.discounts)) {
        return form.discounts.map((value) => String(value ?? ''));
    }
    return DISCOUNT_FORM_FIELDS.map((field) => String(form[field] ?? ''));
}

/**
 * @param {Record<string, unknown>} form
 */
export function syncSystemsListToLegacy(form) {
    const systems = Array.isArray(form.systems) ? [...form.systems] : [];
    SYSTEM_FORM_FIELDS.forEach((field, index) => {
        const value = systems[index];
        form[field] = value ? (normalizeSystemValue(value) || String(value).trim()) : '';
    });
}

/**
 * Serializes the builder systems list for pricing_detail persistence.
 * @param {Record<string, unknown>} form
 * @returns {string[]}
 */
export function systemsArrayForPricingDetail(form) {
    if (Array.isArray(form.systems) && form.systems.length) {
        return form.systems
            .map((value) => normalizeSystemValue(value) || String(value || '').trim())
            .filter(Boolean);
    }
    return systemsArrayFromForm(form);
}

/**
 * Restores systems/subcategories from pricing_detail arrays, with legacy field fallback.
 * When older saves kept extra categories only in category_assignments, those are merged in.
 * @param {Record<string, unknown>} form
 * @param {{ systems?: unknown[], systemSubcategories?: unknown[], system1?: string, system2?: string, system3?: string }} [pricingDetail]
 * @param {Array<{ main_category?: { name?: string }|string, sub_category?: { name?: string }|string }>} [categoryAssignments]
 */
export function hydrateSystemsFromPricingDetail(form, pricingDetail = {}, categoryAssignments = []) {
    const storedSystems = Array.isArray(pricingDetail.systems)
        ? pricingDetail.systems
            .map((value) => normalizeSystemValue(value) || String(value || '').trim())
            .filter(Boolean)
        : [];

    if (storedSystems.length) {
        form.systems = storedSystems;
        syncSystemsListToLegacy(form);
    } else {
        hydrateListFieldsFromLegacy(form);
    }

    const assignmentSystems = [...new Set(
        (Array.isArray(categoryAssignments) ? categoryAssignments : [])
            .map((assignment) => {
                const mainCategory = assignment?.main_category?.name || assignment?.main_category;
                return normalizeSystemValue(mainCategory) || String(mainCategory || '').trim();
            })
            .filter(Boolean),
    )];

    const currentSystems = Array.isArray(form.systems)
        ? form.systems.map((value) => normalizeSystemValue(value) || String(value || '').trim()).filter(Boolean)
        : [];

    if (assignmentSystems.length > currentSystems.length) {
        form.systems = assignmentSystems;
        syncSystemsListToLegacy(form);
    } else if (!currentSystems.length && assignmentSystems.length) {
        form.systems = assignmentSystems;
        syncSystemsListToLegacy(form);
    }

    const storedSubcategories = Array.isArray(pricingDetail.systemSubcategories)
        ? pricingDetail.systemSubcategories.map((value) => String(value ?? '').trim())
        : [];

    if (storedSubcategories.length) {
        form.systemSubcategories = form.systems.map((_, index) => storedSubcategories[index] || '');
    }
}

/**
 * @param {Record<string, unknown>} form
 */
export function syncDiscountsListToLegacy(form) {
    const discounts = Array.isArray(form.discounts) ? [...form.discounts] : [];
    DISCOUNT_FORM_FIELDS.forEach((field, index) => {
        form[field] = discounts[index] ?? '';
    });
}

/**
 * Populates `systems` and `discounts` arrays from legacy scalar fields.
 * @param {Record<string, unknown>} form
 */
export function hydrateListFieldsFromLegacy(form) {
    const systems = SYSTEM_FORM_FIELDS
        .map((field) => normalizeSystemValue(form[field]) || String(form[field] || '').trim())
        .filter(Boolean);
    form.systems = systems.length ? systems : [''];

    if (!Array.isArray(form.systemSubcategories)) {
        form.systemSubcategories = form.systems.map(() => '');
    } else {
        while (form.systemSubcategories.length < form.systems.length) {
            form.systemSubcategories.push('');
        }
        while (form.systemSubcategories.length > form.systems.length) {
            form.systemSubcategories.pop();
        }
    }

    hydrateDiscountsFromLegacy(form);
    hydrateDiscountNotesFromLegacy(form);
}

/**
 * Parses stored discount notes (plain text or JSON array) into form slots.
 * @param {unknown} value
 * @returns {string[]}
 */
export function parseStoredDiscountNotes(value) {
    if (value === null || value === undefined) {
        return [];
    }
    if (Array.isArray(value)) {
        return value.map((entry) => String(entry ?? ''));
    }
    const trimmed = String(value).trim();
    if (!trimmed) {
        return [];
    }
    if (trimmed.startsWith('[')) {
        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                return parsed.map((entry) => String(entry ?? ''));
            }
        } catch {
            // fall through to single note
        }
    }
    return [trimmed];
}

/**
 * Serializes discount notes for API storage (plain text when only one note).
 * @param {unknown[]|unknown} notes
 * @returns {string}
 */
export function serializeStoredDiscountNotes(notes) {
    const entries = Array.isArray(notes)
        ? notes.map((entry) => String(entry ?? '').trim())
        : [String(notes ?? '').trim()];
    if (!entries.some(Boolean)) {
        return '';
    }
    if (entries.length === 1) {
        return entries[0];
    }
    return JSON.stringify(entries);
}

/**
 * @param {unknown[]} [notes]
 * @returns {string[]}
 */
export function normalizeStoredDiscountNotesArray(notes) {
    if (!Array.isArray(notes)) {
        return [];
    }
    return notes.map((entry) => String(entry ?? ''));
}

/**
 * Aligns `discountNotes` array length with the builder `discounts` array.
 * @param {Record<string, unknown>} form
 */
export function hydrateDiscountNotesFromLegacy(form) {
    const notes = parseStoredDiscountNotes(form.discountNotes);
    const discountCount = Array.isArray(form.discounts) ? form.discounts.length : 0;
    const targetLength = Math.max(
        discountCount,
        notes.length,
        notes.some((entry) => String(entry).trim()) ? 1 : 0,
    );
    const aligned = [];
    for (let index = 0; index < targetLength; index += 1) {
        aligned.push(notes[index] ?? '');
    }
    form.discountNotes = aligned;
}

/**
 * Discount slots that should render in the builder (non-zero values or empty slots being edited).
 * @param {unknown[]} [discounts]
 * @returns {boolean}
 */
export function hasVisibleDiscountFields(discounts) {
    if (!Array.isArray(discounts) || !discounts.length) {
        return false;
    }
    return discounts.some((value) => {
        const trimmed = String(value ?? '').trim();
        if (!trimmed) {
            return true;
        }
        return parseNumericInput(trimmed) > 0;
    });
}

/**
 * Loads stored discount rows without zero placeholders from legacy/API data.
 * @param {unknown[]} [discounts]
 * @returns {string[]}
 */
export function normalizeStoredDiscountsArray(discounts) {
    if (!Array.isArray(discounts) || !discounts.length) {
        return [];
    }
    return discounts
        .map((value) => String(value ?? ''))
        .filter((value) => {
            const trimmed = value.trim();
            if (!trimmed) {
                return true;
            }
            return parseNumericInput(trimmed) > 0;
        });
}

/**
 * Populates the builder `discounts` array from legacy scalar discount fields.
 * Expects those fields to already be percentage form values (e.g. "54"),
 * not stored ratios (0.54) — callers convert with ratioToPercentageInput first.
 * @param {Record<string, unknown>} form
 */
export function hydrateDiscountsFromLegacy(form) {
    const discounts = DISCOUNT_FORM_FIELDS.map((field) => {
        const value = form[field];
        if (value === null || value === undefined || value === '') {
            return '';
        }
        return String(value);
    });
    const nonEmptyDiscounts = discounts.filter((value) => {
        const trimmed = String(value).trim();
        if (!trimmed) {
            return false;
        }
        return parseNumericInput(trimmed) > 0;
    });
    form.discounts = nonEmptyDiscounts.length ? nonEmptyDiscounts : [];
}

/**
 * @param {Record<string, unknown>} productBuilderForm
 * @returns {number[]}
 */
function getDiscountRatios(productBuilderForm) {
    if (Array.isArray(productBuilderForm.discounts) && productBuilderForm.discounts.length) {
        return productBuilderForm.discounts
            .map((value) => parseNumericInput(value))
            .filter((ratio) => ratio > 0);
    }
    return [
        parseNumericInput(productBuilderForm.discount1),
        parseNumericInput(productBuilderForm.discount2),
        parseNumericInput(productBuilderForm.cashDiscount),
    ].filter((ratio) => ratio > 0);
}

/** @typedef {'fixed'|'percent'} CostUpliftType */

/**
 * @param {unknown} value
 * @returns {CostUpliftType}
 */
export function normalizeCostUpliftType(value) {
    const normalized = String(value ?? '').trim().toLowerCase();
    return normalized === 'percent' || normalized === '%' || normalized === 'pct'
        ? 'percent'
        : 'fixed';
}

/**
 * Fixed RON/buc total + percent ratio sum for cost uplifts.
 * Percent amounts are ratios (0.05 = 5%) after builderFormToCalculatorInput.
 * @param {Record<string, unknown>} productBuilderForm
 * @returns {{ fixedTotal: number, percentRatio: number }}
 */
export function getCostUpliftParts(productBuilderForm) {
    const amounts = Array.isArray(productBuilderForm?.costUplifts)
        ? productBuilderForm.costUplifts
        : [];
    const types = Array.isArray(productBuilderForm?.costUpliftTypes)
        ? productBuilderForm.costUpliftTypes
        : [];
    let fixedTotal = 0;
    let percentRatio = 0;
    amounts.forEach((value, index) => {
        const amount = parseNumericInput(value);
        if (!(amount > 0)) {
            return;
        }
        const type = normalizeCostUpliftType(types[index]);
        if (type === 'percent') {
            percentRatio += amount;
            return;
        }
        fixedTotal += amount;
    });
    return { fixedTotal, percentRatio };
}

/**
 * Majorare total in RON/buc after entry with transport + stamp.
 * Percent rows apply on `baseRonExVat` (post-transport/FX + stamp).
 * @param {Record<string, unknown>} productBuilderForm
 * @param {number} [baseRonExVat]
 * @returns {number}
 */
export function getCostUpliftRonTotal(productBuilderForm, baseRonExVat = 0) {
    const { fixedTotal, percentRatio } = getCostUpliftParts(productBuilderForm);
    const base = Number.isFinite(baseRonExVat) && baseRonExVat > 0
        ? baseRonExVat
        : 0;
    return fixedTotal + (base * percentRatio);
}

/**
 * Reverse entry RON (with majorare) to base before majorare.
 * entry = base × (1 + percent) + fixed  →  base = (entry − fixed) / (1 + percent)
 * @param {number} entryRonExVat
 * @param {Record<string, unknown>} productBuilderForm
 * @returns {number}
 */
export function entryRonExVatBeforeCostUplift(entryRonExVat, productBuilderForm) {
    const { fixedTotal, percentRatio } = getCostUpliftParts(productBuilderForm);
    const divisor = 1 + percentRatio;
    if (!(divisor > 0)) {
        return roundMoney(entryRonExVat - fixedTotal, 2);
    }
    return roundMoney((entryRonExVat - fixedTotal) / divisor, 2);
}

/**
 * @param {unknown} value
 * @returns {{ type: CostUpliftType, amount: string, note: string }[]}
 */
export function parseStoredCostUplifts(value) {
    if (value === null || value === undefined || value === '') {
        return [];
    }
    let rows = value;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
            return [];
        }
        try {
            rows = JSON.parse(trimmed);
        } catch {
            return [];
        }
    }
    if (!Array.isArray(rows)) {
        return [];
    }
    return rows.map((entry) => {
        if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
            const amount = entry.amount;
            return {
                type: normalizeCostUpliftType(entry.type),
                amount: amount === null || amount === undefined || amount === ''
                    ? ''
                    : String(amount),
                note: String(entry.note ?? ''),
            };
        }
        return {
            type: 'fixed',
            amount: entry === null || entry === undefined || entry === ''
                ? ''
                : String(entry),
            note: '',
        };
    });
}

/**
 * @param {unknown[]} amounts
 * @param {unknown[]} notes
 * @param {unknown[]} [types]
 * @returns {{ type: CostUpliftType, amount: number|null, note: string }[]}
 */
export function serializeCostUpliftsForPayload(amounts, notes = [], types = []) {
    const amountRows = Array.isArray(amounts) ? amounts : [];
    const noteRows = Array.isArray(notes) ? notes : [];
    const typeRows = Array.isArray(types) ? types : [];
    const length = Math.max(amountRows.length, noteRows.length, typeRows.length);
    const rows = [];
    for (let index = 0; index < length; index += 1) {
        const type = normalizeCostUpliftType(typeRows[index]);
        const rawAmount = amountRows[index];
        const note = String(noteRows[index] ?? '').trim();
        const hasAmount = rawAmount !== null
            && rawAmount !== undefined
            && String(rawAmount).trim() !== '';
        let amount = null;
        if (hasAmount) {
            const parsed = parseNumericInput(rawAmount);
            if (parsed > 0) {
                // Form stores percent as display (5); API stores ratio (0.05).
                amount = type === 'percent' ? percentageInputToRatio(parsed) : parsed;
            }
        }
        if ((amount !== null && amount > 0) || note) {
            rows.push({
                type,
                amount: amount !== null && amount > 0 ? amount : null,
                note,
            });
        }
    }
    return rows;
}

/**
 * @param {Record<string, unknown>} form
 * @param {unknown} stored
 */
export function hydrateCostUpliftsFromStored(form, stored) {
    const rows = parseStoredCostUplifts(stored ?? form.costUplifts);
    form.costUplifts = rows.map((row) => {
        if (row.amount === '') {
            return '';
        }
        const numeric = parseNumericInput(row.amount);
        if (!(numeric > 0)) {
            return '';
        }
        if (row.type === 'percent') {
            return ratioToPercentageInput(numeric, 2);
        }
        return formatNumericInputValue(numeric, 2);
    });
    form.costUpliftNotes = rows.map((row) => row.note || '');
    form.costUpliftTypes = rows.map((row) => row.type);
}

/**
 * @param {unknown[]} [costUplifts]
 * @returns {boolean}
 */
export function hasVisibleCostUpliftFields(costUplifts) {
    // Keep rows visible while editing (e.g. typing "0,5") — do not hide on
    // intermediate values that parse as 0.
    return Array.isArray(costUplifts) && costUplifts.length > 0;
}

/**
 * Percentage from form fields (85) → decimal ratio for calculations (0.85).
 * @param {unknown} value
 * @returns {number}
 */
export function percentageInputToRatio(value) {
    return parseNumericInput(value) / 100;
}

/**
 * Stored/API ratios (0.85) → percentage shown in form fields (85.00).
 * @param {unknown} value
 * @param {number} [decimals]
 * @returns {string}
 */
export function ratioToPercentageInput(value, decimals = 2) {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    const numeric = parseNumericInput(value);
    return formatNumericInputValue(roundMoney(numeric * 100, decimals), decimals);
}

/**
 * @param {Record<string, unknown>} form
 * @returns {Record<string, unknown>}
 */
export function builderFormToCalculatorInput(form) {
    const input = { ...form };
    syncSystemsListToLegacy(input);
    syncDiscountsListToLegacy(input);
    PERCENTAGE_RATIO_FORM_FIELDS.forEach((field) => {
        const raw = form[field];
        if (
            field === 'markupActual'
            && (raw === null || raw === undefined || String(raw).trim() === '')
        ) {
            input[field] = null;
            return;
        }
        input[field] = percentageInputToRatio(raw);
    });
    if (Array.isArray(input.discounts)) {
        input.discounts = input.discounts.map((value) => percentageInputToRatio(value));
    }
    if (Array.isArray(input.costUplifts)) {
        const types = Array.isArray(input.costUpliftTypes) ? input.costUpliftTypes : [];
        input.costUplifts = input.costUplifts.map((value, index) => {
            if (normalizeCostUpliftType(types[index]) !== 'percent') {
                return value;
            }
            const raw = value;
            if (raw === null || raw === undefined || String(raw).trim() === '') {
                return value;
            }
            return percentageInputToRatio(raw);
        });
        input.costUpliftTypes = input.costUplifts.map((_, index) => (
            normalizeCostUpliftType(types[index])
        ));
    }
    return input;
}

/**
 * Persist ratio fields for pricing_detail. Empty inputs stay null so an unrelated
 * edit (e.g. denumire furnizor) does not write 0 into untouched discounts.
 * @param {Record<string, unknown>} form
 * @returns {Record<string, number|null>}
 */
export function formRatioFieldsToPricingDetail(form) {
    const ratios = {};
    PERCENTAGE_RATIO_FORM_FIELDS.forEach((field) => {
        const raw = form[field];
        if (raw === null || raw === undefined || String(raw).trim() === '') {
            ratios[field] = null;
            return;
        }
        ratios[field] = percentageInputToRatio(raw);
    });
    return ratios;
}

/**
 * @param {unknown} value
 * @param {number} [length]
 * @returns {string}
 */
export function padSegment(value, length = 2) {
    const normalized = String(value ?? '').trim();
    if (!normalized) {
        return ''.padStart(length, '0');
    }
    if (/^\d+$/.test(normalized)) {
        return normalized.padStart(length, '0');
    }
    return normalized;
}

/**
 * @param {{
 *   chapterCode?: unknown,
 *   subchapterCode?: unknown,
 *   progressiveNumber?: unknown,
 *   manufacturerCode?: unknown,
 * }} params
 * @returns {string}
 */
export function buildProductCode({
    chapterCode,
    subchapterCode,
    progressiveNumber,
    manufacturerCode,
}) {
    const chapterPart = `${String(chapterCode ?? '').trim()}${padSegment(subchapterCode, 2)}`;
    const progressivePart = padSegment(progressiveNumber, 2);
    const manufacturerPart = String(manufacturerCode ?? '').trim();
    if (!chapterPart || !progressivePart || !manufacturerPart) {
        return '';
    }
    return `${chapterPart}.${progressivePart}.${manufacturerPart}`;
}

/**
 * @param {string} currency
 * @param {{
 *   exchangeRateEur?: number,
 *   exchangeRateUsd?: number,
 *   exchangeRatePln?: number,
 *   exchangeRateRon?: number,
 * }} pricingSettings
 * @returns {number}
 */
/**
 * @param {unknown} referencePrice
 * @param {unknown} finalPrice
 * @returns {number}
 */
export function calculatePriceAdjustmentRatio(referencePrice, finalPrice) {
    const reference = parseNumericInput(referencePrice);
    const final = parseNumericInput(finalPrice);
    if (reference <= 0) {
        return 0;
    }
    return roundMoney((final - reference) / reference, 6);
}

/**
 * @param {unknown} referencePrice
 * @param {unknown} adjustmentRatio
 * @returns {number}
 */
export function calculateFinalPriceFromAdjustment(referencePrice, adjustmentRatio) {
    const reference = parseNumericInput(referencePrice);
    const ratio = parseNumericInput(adjustmentRatio);
    if (reference <= 0) {
        return 0;
    }
    return roundMoney(reference * (1 + ratio));
}

/**
 * @param {{
 *   transportCostEstimated?: unknown,
 *   supplierListPriceNew?: unknown,
 *   squareMetersPerTruck?: unknown,
 * }} params
 * @returns {number}
 */
export function calculateTransportCostRate({
    transportCostEstimated,
    supplierListPriceNew,
    squareMetersPerTruck,
}) {
    const estimated = parseNumericInput(transportCostEstimated);
    const supplierPrice = parseNumericInput(supplierListPriceNew);
    const squareMeters = parseNumericInput(squareMetersPerTruck);
    if (estimated <= 0 || supplierPrice <= 0) {
        return 0;
    }
    if (squareMeters > 0) {
        return roundMoney((estimated / squareMeters) / supplierPrice, 6);
    }
    return roundMoney(estimated / supplierPrice, 6);
}

export function resolveExchangeRate(currency, pricingSettings = DEFAULT_PRICING_SETTINGS) {
    const normalizedCurrency = String(currency || 'ron').trim().toLowerCase();
    if (normalizedCurrency === 'euro' || normalizedCurrency === 'eur') {
        return parseNumericInput(pricingSettings.exchangeRateEur ?? DEFAULT_PRICING_SETTINGS.exchangeRateEur);
    }
    if (normalizedCurrency === 'usd') {
        return parseNumericInput(pricingSettings.exchangeRateUsd ?? DEFAULT_PRICING_SETTINGS.exchangeRateUsd);
    }
    if (normalizedCurrency === 'pln') {
        const plnRate = pricingSettings.exchangeRatePln
            ?? pricingSettings.exchangeRateRon
            ?? DEFAULT_PRICING_SETTINGS.exchangeRatePln;
        return parseNumericInput(plnRate);
    }
    const plnRate = pricingSettings.exchangeRatePln
        ?? pricingSettings.exchangeRateRon
        ?? DEFAULT_PRICING_SETTINGS.exchangeRatePln;
    return parseNumericInput(plnRate);
}

/**
 * Excel column AA ("Curs valutar") shows the PLN list rate even on RON rows.
 * RON entry prices still use multiplier 1 (AB = Z); this is display-only parity.
 */
export function resolveDisplayExchangeRate(currency, pricingSettings = DEFAULT_PRICING_SETTINGS) {
    const normalizedCurrency = String(currency || 'ron').trim().toLowerCase();
    if (normalizedCurrency === 'ron') {
        const plnRate = pricingSettings.exchangeRatePln
            ?? pricingSettings.exchangeRateRon
            ?? DEFAULT_PRICING_SETTINGS.exchangeRatePln;
        return parseNumericInput(plnRate);
    }
    return resolveExchangeRate(currency, pricingSettings);
}

/**
 * @param {Record<string, unknown>} productBuilderForm
 * @param {Record<string, unknown>} [pricingSettings]
 * @returns {Record<string, number|string>}
 */
export function calculateProductPricing(productBuilderForm, pricingSettings = DEFAULT_PRICING_SETTINGS) {
    const settings = { ...DEFAULT_PRICING_SETTINGS, ...pricingSettings };
    const vatRate = parseNumericInput(settings.vatRate) || DEFAULT_PRICING_SETTINGS.vatRate;

    const supplierListPriceOld = parseNumericInput(productBuilderForm.supplierListPriceOld);
    const supplierListPriceNew = parseNumericInput(productBuilderForm.supplierListPriceNew);
    const transportCostRate = parseNumericInput(productBuilderForm.transportCostRate);
    const markupReference = parseNumericInput(productBuilderForm.markupReference);
    const listPriceRonIncVatReference = parseNumericInput(productBuilderForm.listPriceRonIncVatReference);
    const currency = String(productBuilderForm.currency || 'ron').trim().toLowerCase();

    const priceChangeRatio = supplierListPriceOld > 0
        ? (supplierListPriceNew / supplierListPriceOld) - 1
        : 0;

    let netEntryPriceExVat = supplierListPriceNew;
    for (const discountRatio of getDiscountRatios(productBuilderForm)) {
        netEntryPriceExVat *= (1 - discountRatio);
    }
    netEntryPriceExVat = roundMoney(netEntryPriceExVat, 4);

    const marketingRatio = parseNumericInput(productBuilderForm.marketingDiscount);
    if (marketingRatio > 0) {
        netEntryPriceExVat = roundMoney(netEntryPriceExVat * (1 + marketingRatio), 4);
    }

    const finalEntryPriceExVat = roundMoney(netEntryPriceExVat * (1 + transportCostRate), 4);
    const appliedExchangeRate = currency === 'ron'
        ? 1
        : resolveExchangeRate(currency, settings);

    const entryPriceRonExVatRaw = currency === 'ron'
        ? finalEntryPriceExVat
        : finalEntryPriceExVat * appliedExchangeRate;
    const stampDutyAmountRon = getStampDutyAmountRon(
        productBuilderForm.stampDutyAmount,
        productBuilderForm.stampDutyCurrency,
        settings,
    );
    // Taxa de timbru după transport/FX; majorările (RON sau %) pe baza acestui preț.
    // Dacă taxa e în altă monedă decât RON, se convertește întâi cu cursul din setări.
    const entryPriceRonBeforeUplift = roundMoney(
        entryPriceRonExVatRaw + stampDutyAmountRon,
        4,
    );
    const costUpliftRonTotal = getCostUpliftRonTotal(
        productBuilderForm,
        entryPriceRonBeforeUplift,
    );
    const entryPriceRonExVat = roundMoney(
        entryPriceRonBeforeUplift + costUpliftRonTotal,
        2,
    );
    const entryPriceRonIncVat = roundMoney(entryPriceRonExVat * vatRate, 2);
    const suggestedListPriceRonIncVatFinal = roundMoney(entryPriceRonIncVat * (1 + markupReference), 2);

    const markupActualRaw = productBuilderForm.markupActual;
    const markupActualProvided = markupActualRaw !== null
        && markupActualRaw !== undefined
        && markupActualRaw !== '';
    const effectiveMarkup = markupActualProvided
        ? parseNumericInput(markupActualRaw)
        : 0;

    // Preț listă RON cu TVA actual:
    // - sticky/manual override when set
    // - otherwise from adaos actual only (do not invent listă from entry alone)
    let listPriceRonIncVatFinal = 0;

    if (productBuilderForm.overrideListPriceRonIncVatFinal) {
        const overrideValue = parseNumericInput(
            productBuilderForm.overrideListPriceRonIncVatFinalValue,
        );
        if (overrideValue > 0) {
            listPriceRonIncVatFinal = roundMoney(overrideValue, 2);
        }
    } else if (markupActualProvided) {
        listPriceRonIncVatFinal = roundMoney(
            entryPriceRonIncVat * (1 + effectiveMarkup),
            2,
        );
    }

    const computedPriceAdjustmentRatio = listPriceRonIncVatReference > 0
        ? calculatePriceAdjustmentRatio(listPriceRonIncVatReference, listPriceRonIncVatFinal)
        : 0;

    // Majorare "din alte coloane": (preț listă nou calculat din adaos − preț referință) / preț referință.
    const suggestedPriceAdjustmentRatio = listPriceRonIncVatReference > 0
        ? calculatePriceAdjustmentRatio(listPriceRonIncVatReference, suggestedListPriceRonIncVatFinal)
        : 0;

    const listPriceRonExVat = listPriceRonIncVatFinal > 0 && vatRate > 0
        ? roundMoney(listPriceRonIncVatFinal / vatRate, 2)
        : 0;

    const listPriceEuroExVat = listPriceRonExVat > 0 && LIST_PRICE_EURO_DIVISOR > 0
        ? roundMoney(listPriceRonExVat / LIST_PRICE_EURO_DIVISOR, 2)
        : 0;

    // Prefer an explicitly stored/edited adaos % (bulk exact %, manual, FX keep-markup).
    // Deriving from 2-decimal sticky listă would rewrite e.g. 117% → ~44,8% / 116,9x
    // when sticky+markup coexist. Sync paths that must refresh adaos after cost/list
    // edits derive from list vs entry money themselves (not via computedMarkupActual).
    const derivedMarkupActual = listPriceRonIncVatFinal > 0 && entryPriceRonIncVat > 0
        ? (listPriceRonIncVatFinal - entryPriceRonIncVat) / entryPriceRonIncVat
        : 0;
    let computedMarkupActual = 0;
    if (markupActualProvided) {
        computedMarkupActual = effectiveMarkup;
    } else if (
        (productBuilderForm.overrideListPriceRonIncVatFinal && listPriceRonIncVatFinal > 0)
        || listPriceRonIncVatFinal > 0
    ) {
        computedMarkupActual = derivedMarkupActual;
    }

    const computedProductCode = productBuilderForm.overrideProductCode
        ? String(productBuilderForm.overrideProductCodeValue || '').trim()
        : buildProductCode(productBuilderForm);

    const displayExchangeRate = resolveDisplayExchangeRate(currency, settings);

    return {
        productCode: computedProductCode,
        priceChangeRatio: roundMoney(priceChangeRatio, 6),
        netEntryPriceExVat,
        finalEntryPriceExVat,
        appliedExchangeRate: roundMoney(appliedExchangeRate, 4),
        displayExchangeRate: roundMoney(displayExchangeRate, 4),
        costUpliftRonTotal: roundMoney(costUpliftRonTotal, 2),
        stampDutyAmountRon: roundMoney(stampDutyAmountRon, 2),
        entryPriceRonExVat,
        entryPriceRonIncVat,
        markupActual: roundMoney(computedMarkupActual, 6),
        computedMarkupActual: roundMoney(computedMarkupActual, 6),
        listPriceRonExVat,
        listPriceEuroExVat,
        listPriceRonIncVatReference: roundMoney(listPriceRonIncVatReference, 2),
        listPriceRonIncVatFinal,
        suggestedListPriceRonIncVatFinal,
        computedPriceAdjustmentRatio,
        suggestedPriceAdjustmentRatio,
    };
}


/** Calc + identity fields edited per furnizor tab (also mirrored on the root form while editing). */
export const FURNIZOR_PRICING_EDIT_FIELDS = [
    'manufacturerProductCode',
    'manufacturerProductName',
    'supplierListPriceOld',
    'supplierListPriceNew',
    'currency',
    'paymentTermDays',
    'discount1',
    'discount2',
    'cashDiscount',
    'marketingDiscount',
    'discounts',
    'transportCostEstimated',
    'transportCostRate',
    'transportCostBreakdown',
    'stampDutyAmount',
    'stampDutyCurrency',
    'stampDutyBreakdown',
    'squareMetersPerTruck',
    'markupReference',
    'markupActual',
    'lockMarkupToReference',
    'listPriceRonIncVatReference',
    'priceAdjustmentRatio',
    'manualListPriceRonIncVatFinal',
    'useManualFinalPrice',
    'overrideListPriceRonIncVatFinal',
    'overrideListPriceRonIncVatFinalValue',
    'notes',
    'discountNotes',
    'costUplifts',
    'costUpliftNotes',
    'costUpliftTypes',
];

let furnizorPricingKeySeq = 0;

/**
 * @returns {string}
 */
export function createFurnizorPricingClientKey() {
    furnizorPricingKeySeq += 1;
    return `fp-${Date.now()}-${furnizorPricingKeySeq}`;
}

/**
 * @param {Partial<Record<string, unknown>>} [overrides]
 * @returns {Record<string, unknown>}
 */
export function createDefaultFurnizorPricingForm(overrides = {}) {
    return {
        id: null,
        clientKey: createFurnizorPricingClientKey(),
        furnizorId: null,
        furnizorCode: '',
        furnizorName: '',
        usesManufacturerAsFurnizor: true,
        isDefault: true,
        manufacturerProductCode: '',
        manufacturerProductName: '',
        supplierListPriceOld: '',
        supplierListPriceNew: '',
        currency: 'ron',
        paymentTermDays: '',
        discount1: '',
        discount2: '',
        cashDiscount: '',
        marketingDiscount: '',
        discounts: [],
        transportCostEstimated: '',
        transportCostRate: '',
        transportCostBreakdown: createDefaultTransportCostBreakdown(),
        stampDutyAmount: '0',
        stampDutyCurrency: 'ron',
        stampDutyBreakdown: createDefaultStampDutyBreakdown(),
        squareMetersPerTruck: '',
        markupReference: '',
        markupActual: '',
        lockMarkupToReference: false,
        listPriceRonIncVatReference: '',
        priceAdjustmentRatio: '',
        manualListPriceRonIncVatFinal: '',
        useManualFinalPrice: false,
        overrideListPriceRonIncVatFinal: false,
        overrideListPriceRonIncVatFinalValue: '',
        notes: '',
        discountNotes: [],
        costUplifts: [],
        costUpliftNotes: [],
        costUpliftTypes: [],
        ...overrides,
    };
}

/**
 * @param {Record<string, unknown>} form
 * @returns {Record<string, unknown>[]}
 */
export function ensureFurnizorPricings(form) {
    if (!Array.isArray(form.furnizorPricings) || !form.furnizorPricings.length) {
        const pricing = createDefaultFurnizorPricingForm({ isDefault: true });
        form.furnizorPricings = [pricing];
        form.activeFurnizorPricingKey = pricing.clientKey;
    }
    if (!form.activeFurnizorPricingKey) {
        const defaultPricing = getDefaultFurnizorPricing(form) || form.furnizorPricings[0];
        form.activeFurnizorPricingKey = defaultPricing.clientKey;
    }
    return form.furnizorPricings;
}

/**
 * @param {Record<string, unknown>} form
 * @returns {Record<string, unknown>|null}
 */
export function getActiveFurnizorPricing(form) {
    const pricings = ensureFurnizorPricings(form);
    return pricings.find((row) => row.clientKey === form.activeFurnizorPricingKey) || pricings[0] || null;
}

/**
 * @param {Record<string, unknown>} form
 * @returns {Record<string, unknown>|null}
 */
export function getDefaultFurnizorPricing(form) {
    const pricings = Array.isArray(form.furnizorPricings) ? form.furnizorPricings : [];
    return pricings.find((row) => row.isDefault) || pricings[0] || null;
}

/**
 * Copies root edit-buffer calc fields into the active furnizor pricing row.
 * @param {Record<string, unknown>} form
 */
export function persistActiveFurnizorPricingFromRoot(form) {
    const active = getActiveFurnizorPricing(form);
    if (!active) {
        return;
    }
    FURNIZOR_PRICING_EDIT_FIELDS.forEach((field) => {
        if (field === 'discounts') {
            active.discounts = Array.isArray(form.discounts) ? [...form.discounts] : [];
            return;
        }
        if (field === 'discountNotes') {
            active.discountNotes = Array.isArray(form.discountNotes)
                ? [...form.discountNotes]
                : [];
            return;
        }
        if (field === 'costUplifts') {
            active.costUplifts = Array.isArray(form.costUplifts)
                ? [...form.costUplifts]
                : [];
            return;
        }
        if (field === 'costUpliftNotes') {
            active.costUpliftNotes = Array.isArray(form.costUpliftNotes)
                ? [...form.costUpliftNotes]
                : [];
            return;
        }
        if (field === 'costUpliftTypes') {
            active.costUpliftTypes = Array.isArray(form.costUpliftTypes)
                ? form.costUpliftTypes.map((type) => normalizeCostUpliftType(type))
                : [];
            return;
        }
        if (field === 'transportCostBreakdown') {
            active.transportCostBreakdown = cloneTransportCostBreakdown(
                form.transportCostBreakdown,
            );
            return;
        }
        if (field === 'stampDutyBreakdown') {
            active.stampDutyBreakdown = cloneStampDutyBreakdown(
                form.stampDutyBreakdown,
            );
            return;
        }
        active[field] = form[field];
    });
}

/**
 * Loads a furnizor pricing row into the root edit buffer.
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>|null} pricing
 */
export function applyFurnizorPricingToRoot(form, pricing) {
    if (!pricing) {
        return;
    }
    FURNIZOR_PRICING_EDIT_FIELDS.forEach((field) => {
        if (field === 'discounts') {
            form.discounts = normalizeStoredDiscountsArray(pricing.discounts);
            return;
        }
        if (field === 'discountNotes') {
            form.discountNotes = Array.isArray(pricing.discountNotes)
                ? normalizeStoredDiscountNotesArray(pricing.discountNotes)
                : parseStoredDiscountNotes(pricing.discountNotes);
            hydrateDiscountNotesFromLegacy(form);
            return;
        }
        if (field === 'costUplifts') {
            const rows = pricing.costUplifts;
            if (
                Array.isArray(rows)
                && rows.some((entry) => entry && typeof entry === 'object' && !Array.isArray(entry))
            ) {
                hydrateCostUpliftsFromStored(form, rows);
            } else {
                form.costUplifts = Array.isArray(rows) ? [...rows] : [];
                form.costUpliftNotes = Array.isArray(pricing.costUpliftNotes)
                    ? [...pricing.costUpliftNotes]
                    : [];
                form.costUpliftTypes = Array.isArray(pricing.costUpliftTypes)
                    ? pricing.costUpliftTypes.map((type) => normalizeCostUpliftType(type))
                    : form.costUplifts.map(() => 'fixed');
            }
            return;
        }
        if (field === 'costUpliftNotes' || field === 'costUpliftTypes') {
            return;
        }
        if (field === 'transportCostBreakdown') {
            form.transportCostBreakdown = cloneTransportCostBreakdown(
                pricing.transportCostBreakdown,
            );
            return;
        }
        if (field === 'stampDutyBreakdown') {
            form.stampDutyBreakdown = cloneStampDutyBreakdown(
                pricing.stampDutyBreakdown,
            );
            return;
        }
        if (Object.prototype.hasOwnProperty.call(pricing, field)) {
            form[field] = pricing[field];
            return;
        }
        if (field === 'currency' || field === 'stampDutyCurrency') {
            form[field] = 'ron';
            return;
        }
        if (field === 'useManualFinalPrice' || field === 'overrideListPriceRonIncVatFinal') {
            form[field] = false;
            return;
        }
        form[field] = '';
    });
    if (!form.stampDutyCurrency) {
        form.stampDutyCurrency = 'ron';
    }
    form.activeFurnizorPricingKey = pricing.clientKey;
}

/**
 * @param {Record<string, unknown>} form
 * @param {string} nextClientKey
 * @param {string|null} [previousClientKey]
 *   When the UI already flipped `activeFurnizorPricingKey` (v-model race),
 *   pass the prior key so we persist into the correct tab before loading next.
 */
export function switchActiveFurnizorPricing(
    form,
    nextClientKey,
    previousClientKey = null,
) {
    const nextKey = String(nextClientKey || '').trim();
    if (!nextKey) {
        return;
    }
    const currentKey = String(form.activeFurnizorPricingKey || '').trim();
    const prevKey = previousClientKey != null
        ? String(previousClientKey).trim()
        : currentKey;

    if (nextKey === prevKey && nextKey === currentKey) {
        return;
    }

    // Persist the tab the user was editing. If the key already moved to next
    // (v-model), temporarily point back at prev so we don't overwrite next.
    if (prevKey && prevKey !== nextKey) {
        form.activeFurnizorPricingKey = prevKey;
        persistActiveFurnizorPricingFromRoot(form);
    } else if (currentKey && currentKey !== nextKey) {
        persistActiveFurnizorPricingFromRoot(form);
    }

    const next = (form.furnizorPricings || []).find((row) => row.clientKey === nextKey);
    if (next) {
        applyFurnizorPricingToRoot(form, next);
    }
}

/**
 * @param {Record<string, unknown>} source
 * @param {Record<string, unknown>} target
 */
function copyFurnizorPricingFields(source, target) {
    FURNIZOR_PRICING_EDIT_FIELDS.forEach((field) => {
        if (field === 'discounts') {
            target.discounts = Array.isArray(source.discounts) ? [...source.discounts] : [];
            return;
        }
        if (field === 'discountNotes') {
            target.discountNotes = Array.isArray(source.discountNotes)
                ? [...source.discountNotes]
                : [];
            return;
        }
        if (field === 'costUplifts') {
            target.costUplifts = Array.isArray(source.costUplifts)
                ? [...source.costUplifts]
                : [];
            return;
        }
        if (field === 'costUpliftNotes') {
            target.costUpliftNotes = Array.isArray(source.costUpliftNotes)
                ? [...source.costUpliftNotes]
                : [];
            return;
        }
        if (field === 'costUpliftTypes') {
            target.costUpliftTypes = Array.isArray(source.costUpliftTypes)
                ? source.costUpliftTypes.map((type) => normalizeCostUpliftType(type))
                : [];
            return;
        }
        if (field === 'transportCostBreakdown') {
            target.transportCostBreakdown = cloneTransportCostBreakdown(
                source.transportCostBreakdown,
            );
            return;
        }
        if (field === 'stampDutyBreakdown') {
            target.stampDutyBreakdown = cloneStampDutyBreakdown(
                source.stampDutyBreakdown,
            );
            return;
        }
        if (Object.prototype.hasOwnProperty.call(source, field)) {
            target[field] = source[field];
        }
    });
}

/**
 * @param {Record<string, unknown>} form
 * @returns {Record<string, unknown>}
 */
export function addFurnizorPricingTab(form) {
    persistActiveFurnizorPricingFromRoot(form);
    const pricings = ensureFurnizorPricings(form);
    const sourcePricing = getActiveFurnizorPricing(form);
    const pricing = createDefaultFurnizorPricingForm({
        isDefault: false,
        usesManufacturerAsFurnizor: false,
    });
    if (sourcePricing) {
        copyFurnizorPricingFields(sourcePricing, pricing);
        // Prices/discounts copy from the previous tab; notes stay empty for the new supplier.
        pricing.notes = '';
        pricing.discountNotes = Array.isArray(pricing.discounts)
            ? pricing.discounts.map(() => '')
            : [];
    }
    pricings.push(pricing);
    applyFurnizorPricingToRoot(form, pricing);
    return pricing;
}

/**
 * @param {Record<string, unknown>} form
 * @param {string} clientKey
 * @returns {boolean}
 */
export function removeFurnizorPricingTab(form, clientKey) {
    const pricings = ensureFurnizorPricings(form);
    if (pricings.length <= 1) {
        return false;
    }
    persistActiveFurnizorPricingFromRoot(form);
    const index = pricings.findIndex((row) => row.clientKey === clientKey);
    if (index < 0) {
        return false;
    }
    const removed = pricings[index];
    pricings.splice(index, 1);
    if (removed.isDefault) {
        pricings[0].isDefault = true;
    }
    const next = pricings.find((row) => row.clientKey === form.activeFurnizorPricingKey) || pricings[0];
    applyFurnizorPricingToRoot(form, next);
    return true;
}

/**
 * @param {Record<string, unknown>} form
 * @param {string} clientKey
 */
export function setDefaultFurnizorPricing(form, clientKey) {
    persistActiveFurnizorPricingFromRoot(form);
    const pricings = ensureFurnizorPricings(form);
    pricings.forEach((row) => {
        row.isDefault = row.clientKey === clientKey;
    });
}

/**
 * Merges structure form with a specific furnizor pricing row for calculator/save.
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>|null} pricing
 * @returns {Record<string, unknown>}
 */
export function mergeFormWithFurnizorPricing(form, pricing) {
    if (!pricing) {
        return { ...form };
    }
    // Shallow copy is enough for scalar root fields, but never share the
    // furnizorPricings array — applyFurnizorPricingToRoot must not be able to
    // retarget persist onto the live form's tabs via a shared reference.
    const merged = {
        ...form,
        furnizorPricings: Array.isArray(form.furnizorPricings)
            ? [...form.furnizorPricings]
            : [],
    };
    applyFurnizorPricingToRoot(merged, pricing);
    return merged;
}

/**
 * @returns {Record<string, unknown>}
 */
export function createDefaultProductBuilderForm() {
    const defaultPricing = createDefaultFurnizorPricingForm({ isDefault: true });
    return {
        systems: [''],
        systemSubcategories: [''],
        discounts: [],
        system1: '',
        system2: '',
        system3: '',
        chapterCode: '',
        chapterName: '',
        subchapterCode: '',
        subchapterName: '',
        progressiveNumber: '',
        manufacturerName: '',
        manufacturerCode: '',
        serialNumber: false,
        manufacturerProductCode: '',
        manufacturerProductName: '',
        productName: '',
        measurementUnit: 'm²',
        weight: '',
        nomenclatureCode: '',
        productNotes: '',
        commercialPolicyDiscount: '',
        isCompositeProduct: false,
        componentProductCodes: [],
        componentProductsKeepInList: [],
        visibleInOfferApp: true,
        status: null,
        supplierListPriceOld: '',
        supplierListPriceNew: '',
        currency: 'ron',
        paymentTermDays: '',
        discount1: '',
        discount2: '',
        cashDiscount: '',
        marketingDiscount: '',
        transportCostEstimated: '',
        transportCostRate: '',
        transportCostBreakdown: createDefaultTransportCostBreakdown(),
        stampDutyAmount: '0',
        stampDutyCurrency: 'ron',
        stampDutyBreakdown: createDefaultStampDutyBreakdown(),
        squareMetersPerTruck: '',
        markupReference: '',
        markupActual: '',
        lockMarkupToReference: false,
        listPriceRonIncVatReference: '',
        priceAdjustmentRatio: '',
        manualListPriceRonIncVatFinal: '',
        useManualFinalPrice: false,
        overrideProductCode: false,
        overrideProductCodeValue: '',
        overrideListPriceRonIncVatFinal: false,
        overrideListPriceRonIncVatFinalValue: '',
        notes: '',
        discountNotes: [],
        costUplifts: [],
        costUpliftNotes: [],
        costUpliftTypes: [],
        categories: [],
        priceListIds: [],
        furnizorPricings: [defaultPricing],
        activeFurnizorPricingKey: defaultPricing.clientKey,
    };
}
